import React, { useState, useEffect } from 'react';
import { X, Folder } from 'lucide-react';
import useModal from '../../hooks/useModal';

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Orange
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#EC4899', // Pink
  '#6B7280', // Gray
];

const NewFolderModal = ({ isOpen, onClose, onSave, caseId, parentFolder, editingFolder }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    icon: 'folder',
    case_id: null,
    parent_folder_id: null
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editingFolder) {
        // Editing existing folder - don't include case_id in update
        setFormData({
          name: editingFolder.name || '',
          description: editingFolder.description || '',
          color: editingFolder.color || '#3B82F6',
          icon: editingFolder.icon || 'folder',
          parent_folder_id: editingFolder.parent_folder_id || null
        });
      } else {
        // Creating new folder - include case_id
        setFormData({
          name: '',
          description: '',
          color: '#3B82F6',
          icon: 'folder',
          case_id: caseId,
          parent_folder_id: parentFolder?.id || null
        });
      }
      setError('');
    }
  }, [isOpen, editingFolder, parentFolder, caseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save folder');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      {/* Backdrop that doesn't cover navbar */}
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingFolder ? 'Edit Folder' : 'New Folder'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Parent Folder Info */}
          {parentFolder && !editingFolder && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <p className="text-sm text-gray-800">
                Creating subfolder in: <span className="font-semibold">{parentFolder.name}</span>
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Folder Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Folder Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="e.g., Client Documents"
              maxLength={255}
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="Brief description of folder contents"
              rows={3}
            />
          </div>

          {/* Color Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="flex gap-2 flex-wrap">
              {PRESET_COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`
                    w-8 h-8 rounded-full border-2 transition-all
                    ${formData.color === color ? 'border-gray-900 scale-110' : 'border-gray-300'}
                  `}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <p className="text-sm text-gray-600 mb-2">Preview:</p>
            <div className="flex items-center gap-2">
              <Folder className="h-5 w-5" style={{ color: formData.color }} />
              <span className="font-medium text-gray-900">{formData.name || 'Folder Name'}</span>
            </div>
            {formData.description && (
              <p className="text-sm text-gray-600 mt-2">{formData.description}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 hover:bg-yellow-600 rounded-md disabled:opacity-50"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : editingFolder ? (
                'Save Changes'
              ) : (
                'Create Folder'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewFolderModal;
